import React, { useEffect } from 'react';
import { Text, styled, Container, ContainerPadding, TextAppearance } from '@volkswagen-onehub/components-core';
import { OneElementTemplate } from 'src/feature-app/Screen';
import { ConfirmacionTaigo } from './ConfirmacionTaigo';
import { 
  Email, 
  PantallaNombreApellido, 
  Telefono, 
  MultipleCTASelection, 
  EdadInputText,
  SuggestModelo,
  ContactInfoWrapper
} from 'src/components';
import { useDispatch, useStore, useSelector } from 'react-redux';
import { dpsSend } from 'src/bff';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import {
  FormController,
  removeWhiteSpace,
  useFeatureAppConfig,
  useOneFormContext,
  formatPrefix,
} from 'src/feature-app';
import { ThankYouTaigo } from './ThankYouTaigo';
import { formatHeaders, formDataBuilder } from 'src/forms/format-fields';
import { CTAsMotivacionCompraTaigo, CTAsDesplazamientoTaigo, CTAsOtroModeloTaigo } from 'src/feature-app/InputsValues';
import { addRemovedStep, getIndexInSteps, removeStep, resetStepsScreenIndex } from 'src/forms/helpers/add-remove-steps';

export enum LanzamientoTaigoSteps {
  NombreApellido,
  EmailTelefono,
  TienesVolkswagen,
  Desplazamiento,
  Edad,
  MotivacionCompra,
  OtroModelo,
  Modelo,
  Confirmacion,
}

interface LanzamientoTaigoFormProps {
  updatedFormData?: Partial<FormDataGroup>;
}

export function LanzamientoTaigoForm({ updatedFormData }: LanzamientoTaigoFormProps) {
  const { initializeForm, handleNextStep } = useOneFormContext();
  const { formData, formInfo } = useSelector((state: OneFormState) => state);
  const { screenIndex } = formInfo;
  const { otroModelo } = formData.fields;
  const store = useStore();
  const config = useFeatureAppConfig();
  const dispatch = useDispatch();

  const handleMasInformacion = (otroModelo: string) => {
    const { steps: actualSteps } = store.getState().formInfo;

    let newSteps = [...actualSteps];

    if (otroModelo === 'true') {
      const stepIndex = getIndexInSteps(newSteps, 'Modelo');

      if (stepIndex === -1) {
        newSteps = addRemovedStep(steps, actualSteps, 'Modelo', screenIndex);
        setTimeout(() => {
          handleNextStep();
        }, 0);
      }
    } else {
      newSteps = removeStep(newSteps, 'Modelo');
    }

    const newStepsWithIndex = resetStepsScreenIndex(newSteps);

    dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
    dispatch({ type: 'UPDATE_LAST_STEP', payload: newStepsWithIndex[newStepsWithIndex.length - 1] });
    dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: newStepsWithIndex.length });
  };

  const steps: Steps[] = [
    {
      title: (
        <>
          Para ayudarte a vivirlo todo, necesitamos saber <Text bold>quién eres</Text>
        </>
      ),
      fields: (
        <OneElementTemplate
          element={
            <div style={{ marginBottom: '2px' }}>
              <PantallaNombreApellido />
            </div>
          }
        />
      ),
      screenIndex: LanzamientoTaigoSteps.NombreApellido,
      name: 'NombreApellido',
      outputs: ['name', 'surname', 'secondSurname'],
      outputsText: ['name', 'surname', 'secondSurname'],
    },
    {
      title: (
        <>
          Si ganas… <Text bold>¿Cómo contactamos contigo?</Text>
        </>
      ),
      fields: (
        <OneElementTemplate
          element={
            <div>
              <ContactInfoWrapper>
                <Telefono paginaConfirmacion={false} required={true} />
                <Email paginaConfirmacion={false} required={true} paddingTop='24px'/>
              </ContactInfoWrapper>
              <Container padding={{top: ContainerPadding.static200}}>
                <Text appearance={TextAppearance.copy150} staticSize>
                <sup>*</sup>Para participar debes ser mayor de edad y tener carnet de conducir vigente
                </Text>
            </Container>
            </div>
          }
        />
      ),
      screenIndex: LanzamientoTaigoSteps.EmailTelefono,
      name: 'EmailTelefono',
      outputs: ['cellphone', 'email'],
      outputsText: ['cellphone', '/', 'email'],
    },
    {
      title: (
        <>
         Sabemos que te atreves a ir a contracorriente,{' '}<Text bold>pero no cómo te desplazas en tu día a{' '} 
            <span style={{ whiteSpace: 'nowrap' }}>
              día.<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplate
          element={<MultipleCTASelection ctaValues={CTAsDesplazamientoTaigo} name="desplazamiento" nextOnClick />}
        />
      ),
      screenIndex: LanzamientoTaigoSteps.Desplazamiento,
      name: 'Desplazamiento',
      outputs: ['desplazamiento'],
      outputsText: ['desplazamiento'],
      trueOrFalseQuestion: true,
    },
    {
      title: (
        <>
          Dicen que para escaparse de lo convencional no hay edad, ¿o sí?{' '}<Text bold>¿Cuál es la{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              tuya?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: <OneElementTemplate element={<div style={{ marginBottom: '2px' }}><EdadInputText /></div>} />,
      screenIndex: LanzamientoTaigoSteps.Edad,
      name: 'Edad',
      outputs: ['edad'],
      outputsText: ['edad'],
    },
    {
      title: (
        <>
          Te sobran los motivos para vivirlo todo con el nuevo Taigo{' '}<Text bold>¿se te ocurre alguno{' '} 
            <span style={{ whiteSpace: 'nowrap' }}>
              más?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplate
          element={<MultipleCTASelection ctaValues={CTAsMotivacionCompraTaigo} name="motivacionCompra" nextOnClick />}
        />
      ),
      screenIndex: LanzamientoTaigoSteps.MotivacionCompra,
      name: 'MotivacionCompra',
      outputs: ['motivacionCompra'],
      outputsText: ['motivacionCompra'],
    },
    {
      title: (
        <>
          Y hablando de vivir, además del Taigo,{' '}<Text bold>¿elegirías otro modelo para vivirlo{' '} 
            <span style={{ whiteSpace: 'nowrap' }}>
              todo?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplate
          element={<MultipleCTASelection ctaValues={CTAsOtroModeloTaigo} name="otroModelo" nextOnClick />}
        />
      ),
      screenIndex: LanzamientoTaigoSteps.OtroModelo,
      name: 'OtroModelo',
      outputs: ['otroModelo'],
      outputsText: ['otroModelo'],
      trueOrFalseQuestion: true,
    },
    {
      title: (
        <>
          <Text bold>¿Y qué otros modelos/marcas estás{' '}  
            <span style={{ whiteSpace: 'nowrap' }}>
            valorando?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        
        <div style={{ marginBottom: '2px' }}>
          <SuggestModelo notALayer={false} fieldsName="modelo" required outputsSeparados />
        </div>
  
      ),
      screenIndex: LanzamientoTaigoSteps.Modelo,
      name: 'Modelo',
      outputs: ['modelo'],
      outputsText: ['modelo'],
    },
    {
      fields: <ConfirmacionTaigo />,
      screenIndex: LanzamientoTaigoSteps.Confirmacion,
      name: 'Confirmacion',
      isLastStep: true,
      hidePreviousStep: true,
    },
  ];

  const NuevoFormData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW',
      DESC_VN_VO: 'VN',
      DESC_GDPR_ADAPTED: 'true',
      DESC_TOKEN: 'VW_DDB_FRM_SORTEO_TAIGO',
      DESC_ID_CAMPAIGN: 'pendiente',
      DESC_FORM_DETAIL: 'Sorteo Taigo 2022',
      DESC_CAMPAIGNBLOCK: 'Pendiente',
      DESC_FORMOBJECTIVE: 'Concurso',
    },
    fields: {
      formName: 'adhoc-2022-sorteo-taigo',
      formType: 'otherform',
      pageCategory: 'Concurso',
      ...updatedFormData?.fields,
    }, // Analítica
  };

  const getFormInfo = (): {
    firstStep: Steps;
    lastStep: Steps;
    stepsHistory: number[];
    numberOfScreens: number;
    newSteps?: Steps[];
  } => {
    return {
      firstStep: steps[0],
      lastStep: steps[steps.length - 1],
      stepsHistory: [0],
      numberOfScreens: steps.length,
      newSteps: null,
    };
  };

  const sendForm = (): Promise<boolean> => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    
    const { fields, headers } = formData;

    return new Promise<any>(async (resolve, reject) => {
      const { tc, today, referalUrl, url, device } = getParamsForm();

      const data = {
        'headers[COD_FORM_ID]': `vw-oneform-${fields.formName}`,
        'headers[DESC_URLORIGIN]': url,
        'headers[DESC_DEVICETYPE]': device,
        ...formatHeaders(headers),

        'headers[FECHA_INSERTION_DATE]': today,
        'headers[TC]': tc,

        'fields[DESC_NAME]': fields.name,
        'fields[DESC_SURNAME_1]': fields.surname,
        'fields[DESC_SURNAME_2]': fields.secondSurname,
        'fields[DESC_EMAIL_ADDRESS]': removeWhiteSpace(fields.email),
        'fields[NUM_CELLPHONE]': `${formatPrefix(fields.prefix)}${fields.cellphone}`,
        'fields[DESC_AUXFIELD1]': fields.estiloExcusa, //estilo de escusa deportivo/urbano
        'fields[DESC_AUXFIELD2]': fields.excusa, // escusa elegida
        'fields[DESC_AUXFIELD3]': fields.desplazamiento,
        'fields[DESC_AUXFIELD4]': fields.edad,
        'fields[DESC_AUXFIELD5]': fields.motivacionCompra,
        'fields[DESC_AUXFIELD6]': fields.brand === 'false' ? null : fields.brand,
        'fields[DESC_AUXFIELD7]': fields.model === 'false' ? null : fields.model,
        'fields[DESC_FORM_VERSION]': `vw-oneform-adhoc_2022_sorteo_taigo-${process.env.VERSION}`,
        'fields[DESC_LOPD]': 'PERMITE',
        'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO',
        'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
        'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
      };
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response && response.data && response.data.content && response.data.content.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  useEffect(() => {
    const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } = getFormInfo();

    const NuevoFormInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens,
      formTheme: 'inverted',
      screenIndex: firstStep.screenIndex,
      step: firstStep,
      steps: newSteps ? newSteps : steps,
      stepsHistory,
      thankYouComponent: <ThankYouTaigo />,
    };

    initializeForm(NuevoFormInfo, NuevoFormData);
  }, []);

  useEffect(()=>{
    handleMasInformacion(otroModelo);
  }, [otroModelo]);

  return <FormController steps={steps} screenType="layer-screen" />;
}
